// apps/web/src/stores/chat-store.ts
import { create } from 'zustand'

interface OnlineUser {
  user_type: string
  user_id: number
}

interface TypingUser {
  user_type: string
  user_id: number
  name: string
}

interface ChatState {
  activeConversationId: string | null
  setActiveConversation: (id: string | null) => void

  unreadCounts: Record<string, number>
  setUnreadCount: (conversationId: string, count: number) => void
  incrementUnread: (conversationId: string) => void
  clearUnread: (conversationId: string) => void

  onlineUsers: OnlineUser[]
  setOnlineUsers: (users: OnlineUser[]) => void
  addOnlineUser: (user: OnlineUser) => void
  removeOnlineUser: (userType: string, userId: number) => void

  typingUsers: Record<string, TypingUser[]>
  addTypingUser: (conversationId: string, user: TypingUser) => void
  removeTypingUser: (conversationId: string, userType: string, userId: number) => void

  isConnected: boolean
  setConnected: (connected: boolean) => void
}

export const useChatStore = create<ChatState>((set) => ({
  activeConversationId: null,
  setActiveConversation: (id) => set({ activeConversationId: id }),

  unreadCounts: {},
  setUnreadCount: (conversationId, count) =>
    set((s) => ({ unreadCounts: { ...s.unreadCounts, [conversationId]: count } })),
  incrementUnread: (conversationId) =>
    set((s) => ({
      unreadCounts: {
        ...s.unreadCounts,
        [conversationId]: (s.unreadCounts[conversationId] ?? 0) + 1,
      },
    })),
  clearUnread: (conversationId) =>
    set((s) => {
      const counts = { ...s.unreadCounts }
      delete counts[conversationId]
      return { unreadCounts: counts }
    }),

  onlineUsers: [],
  setOnlineUsers: (users) => set({ onlineUsers: users }),
  addOnlineUser: (user) =>
    set((s) => {
      const exists = s.onlineUsers.some(
        (u) => u.user_type === user.user_type && u.user_id === user.user_id,
      )
      if (exists) return s
      return { onlineUsers: [...s.onlineUsers, user] }
    }),
  removeOnlineUser: (userType, userId) =>
    set((s) => ({
      onlineUsers: s.onlineUsers.filter(
        (u) => !(u.user_type === userType && u.user_id === userId),
      ),
    })),

  typingUsers: {},
  addTypingUser: (conversationId, user) =>
    set((s) => {
      const current = s.typingUsers[conversationId] ?? []
      const exists = current.some(
        (u) => u.user_type === user.user_type && u.user_id === user.user_id,
      )
      if (exists) return s
      return { typingUsers: { ...s.typingUsers, [conversationId]: [...current, user] } }
    }),
  removeTypingUser: (conversationId, userType, userId) =>
    set((s) => ({
      typingUsers: {
        ...s.typingUsers,
        [conversationId]: (s.typingUsers[conversationId] ?? []).filter(
          (u) => !(u.user_type === userType && u.user_id === userId),
        ),
      },
    })),

  isConnected: false,
  setConnected: (connected) => set({ isConnected: connected }),
}))
